import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { mapDatasetFiltersToPayload } from "@/helpers/datasetAttributeHelper";
import {
  DatasetData,
  DatasetFilter,
  DatasetFilterPayload,
  H3Configuration
} from "@/types/dataset";
import {
  AddWidgetPayload,
  PostAnalysisWidgetAnalyticsPayload,
  PostWidgetAnalysisConfigPayload,
  PutAnalysisWidgetConfigItem,
  PutAnalysisWidgetConfigPayload,
  WidgetConfig,
  WidgetConfigWithoutWidgetId,
  WidgetDefaultConfiguration,
  WidgetType
} from "@/types/widget";

// Per-chart-type UI placeholder labels. An empty string means the corresponding
// field is not used by that chart type — the form hides it, and the analysis
// payload omits it (see mapPostWidgetAnalysisConfigPayload).
export type PlaceholderMap = {
  dimension: string;
  measure: string;
  dimension_values_shown: string;
  group_by: string;
};

export const placeHolderMap: Record<WidgetType, PlaceholderMap> = {
  [WidgetType.BAR_H]: {
    dimension: "Dimension (Y axis)",
    measure: "Measure (X axis)",
    dimension_values_shown: "Bar Shown",
    group_by: "Group By"
  },
  [WidgetType.BAR_V]: {
    dimension: "Dimension (X axis)",
    measure: "Measure (Y axis)",
    dimension_values_shown: "Bar Shown",
    group_by: "Group By"
  },
  [WidgetType.DONUT]: {
    dimension: "Dimension (Slice)",
    measure: "Measure (Value)",
    dimension_values_shown: "No of Slice",
    group_by: "Order By"
  },
  [WidgetType.LINE]: {
    dimension: "Dimension (X axis)",
    measure: "Measure (Y axis)",
    dimension_values_shown: "",
    // Non-empty group_by acts as the "supports ordering" sentinel so the
    // order_by value is forwarded in the analysis payload (ORB-3440).
    group_by: "Order By"
  },
  [WidgetType.BIG_NUMBER]: {
    dimension: "",
    measure: "Measure",
    dimension_values_shown: "",
    group_by: ""
  }
};

// An empty `dimension_values_shown` placeholder means the form hides the
// control, so no limit can be chosen and none must be applied.
export const chartTypeHasRowLimit = (chartType: WidgetType): boolean =>
  !!placeHolderMap[chartType].dimension_values_shown;

export type WidgetGridBreakpoint = 768 | 1024 | 1366 | 1920 | 3840;

export type WidgetGridSection = {
  type: "bn" | "chart";
  widgets: WidgetConfig[];
};

// chart tiers: [1, 2, 3-4, 5-8, 9+] widgets; bn tiers: [1, 2, 3, 4+] widgets
const COL_RULES: Record<
  WidgetGridBreakpoint,
  { chart: number[]; bn: number[] }
> = {
  768: { chart: [1, 1, 2, 2, 2], bn: [2, 2, 3, 3] },
  1024: { chart: [1, 2, 2, 2, 2], bn: [2, 2, 3, 3] },
  1366: { chart: [1, 2, 2, 2, 2], bn: [3, 2, 3, 4] },
  1920: { chart: [1, 2, 2, 2, 2], bn: [3, 2, 3, 4] },
  3840: { chart: [1, 2, 3, 4, 4], bn: [4, 4, 4, 6] }
};

export const getWidgetGridBreakpoint = (
  width: number
): WidgetGridBreakpoint => {
  if (width >= 3840) return 3840;
  if (width >= 1920) return 1920;
  if (width >= 1366) return 1366;
  if (width >= 1024) return 1024;
  return 768;
};

// Consecutive BN widgets share one grid row; chart widgets do the same.
// A chart between BN groups splits them into separate sections so each
// section's column count is driven only by its own widget count.
export const groupWidgetsBySection = (
  widgets: WidgetConfig[]
): WidgetGridSection[] => {
  const sections: WidgetGridSection[] = [];
  for (const widget of widgets) {
    const type = widget.chart_type === WidgetType.BIG_NUMBER ? "bn" : "chart";
    const last = sections.at(-1);
    if (last?.type === type) last.widgets.push(widget);
    else sections.push({ type, widgets: [widget] });
  }
  return sections;
};

const getSectionCols = (
  section: WidgetGridSection,
  bp: WidgetGridBreakpoint
): number => {
  const count = section.widgets.length;
  const rules = COL_RULES[bp];
  if (section.type === "bn") {
    if (count === 1) return rules.bn[0];
    if (count === 2) return rules.bn[1];
    if (count === 3) return rules.bn[2];
    return rules.bn[3];
  }
  if (count === 1) return rules.chart[0];
  if (count === 2) return rules.chart[1];
  if (count <= 4) return rules.chart[2];
  if (count <= 8) return rules.chart[3];
  return rules.chart[4];
};

// BN sections fill the last row with empty divs so a single BN never spans full
// width. Chart sections rely on CSS grid's natural placement (no empties).
export const getWidgetSectionLayout = (
  section: WidgetGridSection,
  bp: WidgetGridBreakpoint
) => {
  const cols = getSectionCols(section, bp);
  const remainder = section.widgets.length % cols;
  const emptySlots =
    section.type === "bn" && remainder > 0 ? cols - remainder : 0;
  return { cols, emptySlots };
};

export const getColumnDisplayName = (
  rawDatasets: { [id: string]: DatasetData },
  datasetId: string,
  columnCode: string
): string =>
  rawDatasets[datasetId]?.columns.find((c) => c.code === columnCode)?.name ??
  columnCode;

export const buildAnalysisAreaFilter = (
  gridsSelected: string[],
  rawDatasets: { [id: string]: DatasetData },
  datasetId: string
): DatasetFilterPayload[] => {
  const isAll =
    gridsSelected.length === 0 || gridsSelected.includes(DROPDOWN_ALL_VALUE);
  if (isAll) return [];

  // widget.dataset_id is suffixed (e.g. "ds-123_1") but rawDatasets keys are
  // the bare backend ID (e.g. "ds-123") — strip the suffix before lookup.
  const lastUnderscore = datasetId.lastIndexOf("_");
  const bareDatasetId =
    lastUnderscore === -1 ? datasetId : datasetId.slice(0, lastUnderscore);

  const refColumn = rawDatasets[bareDatasetId]?.columns.find(
    (col) => col.is_reference_column
  );
  if (!refColumn?.schema_id) return [];

  return [{ id: refColumn.schema_id, filter: gridsSelected }];
};

// Redux stores dataset IDs with an instance suffix (e.g. "uuid_1"). All API
// payloads must use the bare backend UUID without the suffix.
const stripDatasetSuffix = (id: string): string => {
  const last = id.lastIndexOf("_");
  return last === -1 ? id : id.slice(0, last);
};

export const mapPostWidgetAnalysisConfigPayload = (
  data: WidgetDefaultConfiguration,
  filters: DatasetFilter[],
  h3Config?: H3Configuration | null,
  groupBy?: string
): PostWidgetAnalysisConfigPayload => {
  const mappedFilter = mapDatasetFiltersToPayload(filters);
  const datasetId = stripDatasetSuffix(data.dataset_id);

  // Only forward fields that the current chart type actually supports — i.e.
  // those with a non-empty placeholder. Fields whose placeholder is empty
  // (e.g. `dimension` for BIG_NUMBER, `order_by` / `group_by` for LINE) are
  // dropped from the payload so the backend never receives stale form values
  // that the user couldn't see or edit in the UI.
  const placeholders = placeHolderMap[data.chart_type];

  return {
    id: datasetId,
    aggregation: data.aggregation,
    chart_type: data.chart_type,
    ...(placeholders.dimension && { dimension: data.dimension }),
    ...(placeholders.measure && { measure: data.measure }),
    // group_by doubles as the "has ordering" sentinel: when present in the placeholder,
    // it means this chart type also supports order_by (e.g. DONUT uses group_by for ordering).
    ...(placeholders.group_by && { order_by: data.configuration.order_by }),
    ...(filters && { filters: mappedFilter }),
    ...(!!h3Config && {
      grid_config: {
        level: h3Config?.resolution,
        aggregation_type: h3Config.aggregationType
      }
    }),
    ...(!!groupBy && groupBy !== "none" && { group_by: groupBy }),
    ...(chartTypeHasRowLimit(data.chart_type) && {
      limit: data.configuration.dimension_values_shown
    })
  };
};

export const mapWidgetsToPutAnalysisConfig = (
  widgetList: WidgetConfigWithoutWidgetId[],
  gridsSelected: string[]
): PutAnalysisWidgetConfigPayload => ({
  filter_by_catchment_ids:
    gridsSelected.length === 0 || gridsSelected.includes(DROPDOWN_ALL_VALUE)
      ? []
      : gridsSelected,
  widgets: widgetList.map(
    (widget): PutAnalysisWidgetConfigItem => ({
      dataset_id: stripDatasetSuffix(widget.dataset_id),
      label: widget.label,
      chart_type: widget.chart_type,
      dimension: widget.dimension,
      measure: widget.measure,
      aggregation: widget.aggregation,
      configuration: { ...widget.configuration },
      ...(widget.group_by &&
        widget.group_by !== "none" && {
          group_by: widget.group_by
        })
    })
  )
});

export const mapPostAnalysisWidgetAnalyticsPayload = (
  data: WidgetDefaultConfiguration,
  analysisId: string,
  areaFilters: DatasetFilterPayload[],
  datasetFilters: DatasetFilter[]
): PostAnalysisWidgetAnalyticsPayload => {
  const datasetId = stripDatasetSuffix(data.dataset_id);

  const placeholders = placeHolderMap[data.chart_type];

  // The catchment-area filter and the dataset's own attribute filters are both
  // sent as one list — the backend ANDs every entry.
  const filters = [
    ...areaFilters,
    ...mapDatasetFiltersToPayload(datasetFilters)
  ];

  return {
    analysisId,
    datasetId,
    aggregation: data.aggregation,
    chart_type: data.chart_type,
    ...(placeholders.dimension && { dimension: data.dimension }),
    ...(placeholders.measure && { measure: data.measure }),
    // group_by doubles as the "has ordering" sentinel: when present in the placeholder,
    // it means this chart type also supports order_by (e.g. DONUT uses group_by for ordering).
    ...(placeholders.group_by && { order_by: data.configuration.order_by }),
    filters,
    ...(!!data.h3Configuration && {
      grid_config: {
        level: data.h3Configuration.resolution,
        aggregation_type: data.h3Configuration.aggregationType
      }
    }),
    ...(placeholders.group_by &&
      !!data.group_by &&
      data.group_by !== "none" && { group_by: data.group_by }),
    ...(chartTypeHasRowLimit(data.chart_type) && {
      limit: data.configuration.dimension_values_shown
    })
  };
};

/** Dataset instance a saved widget re-attaches to when a project is restored. */
export interface WidgetRestoreTarget {
  datasetInstanceId: string;
  /** Dataset display name, used when the saved widget carries no label. */
  defaultLabel: string;
}

/**
 * `addWidget` payloads for a restore, emitted bottom-up because saved widgets
 * come in display order while `addWidget` prepends to the visualization order.
 */
export const buildWidgetRestorePayloads = (
  savedWidgets: WidgetDefaultConfiguration[],
  targets: Map<WidgetDefaultConfiguration, WidgetRestoreTarget>
): AddWidgetPayload[] =>
  [...savedWidgets].reverse().flatMap((savedWidget) => {
    const target = targets.get(savedWidget);
    if (!target) return [];

    return [
      {
        id: target.datasetInstanceId,
        widget: {
          ...savedWidget,
          is_default: savedWidget.is_default ?? false,
          label: savedWidget.label ? savedWidget.label : target.defaultLabel,
          dataset_id: target.datasetInstanceId
        }
      }
    ];
  });
